export const queryKeys = {
  // Users
  user: (userId: string) => ["user", userId] as const,
  users: () => ["users"] as const,
  usersSearch: (query: string) => ["users-search", query] as const,
  adminUsersCurrent: () => ["admin-users-current"] as const,
  adminUsersCount: () => ["admin-users-count"] as const,
  adminUsersHistory: (days: number) => ["admin-users-history", days] as const,
  adminUsersHistoryAll: () => ["admin-users-history-all"] as const,
  userBadges: (userId: string) => ["user-badges", userId] as const,
  userBlocks: (userId: string) => ["user-blocks", userId] as const,
  userCards: (userId: string) => ["user-cards", userId] as const,
  userCardsLookup: (userId: string) => ["user-cards-lookup", userId] as const,
  userCounters: (userId: string, params: unknown) =>
    ["user-counters", userId, params] as const,
  userCreditLedger: (userId?: string, page?: number) =>
    userId !== undefined
      ? (["user-credit-ledger", userId, page] as const)
      : (["user-credit-ledger"] as const),
  userLoginTimeHeatmap: (userId: string, days?: number) =>
    ["user-login-time-heatmap", userId, days] as const,
  globalLoginTimeHeatmap: (days: number | undefined, ignoreSpecialUsers: boolean) =>
    ["global-login-time-heatmap", days, ignoreSpecialUsers] as const,
  globalLoginsPerDay: (days: number | undefined, ignoreSpecialUsers: boolean) =>
    ["global-logins-per-day", days, ignoreSpecialUsers] as const,
  userLoginHistoryAll: (userId: string) =>
    ["user-login-history-all", userId] as const,

  // Badges
  badges: (page?: number) => (page !== undefined ? ["badges", page] : ["badges"]) as const,
  badgeVariants: (badgeId: string) => ["badge-variants", badgeId] as const,
  badgeVariantTypes: () => ["badge-variant-types"] as const,

  // Profile cards
  profileCards: () => ["profile-cards"] as const,

  // Events
  adminEvents: () => ["admin-events"] as const,
  clubsRecommended: (params: unknown) => ["clubs-recommended", params] as const,
  usersRecommended: (params: unknown) => ["users-recommended", params] as const,
  eventsRecommended: (params: unknown) => ["events-recommended", params] as const,
  eventParticipants: (eventId: string) => ["event-participants", eventId] as const,
  eventBoosts: (eventId: string) => ["event-boosts", eventId] as const,
  eventBadges: (eventId: string) => ["event-badges", eventId] as const,
  eventTiers: () => ["event-tiers"] as const,

  // Feature flags
  featureFlags: () => ["feature-flags"] as const,
  featureFlagOverrides: (flagKey: string) => ["feature-flag-overrides", flagKey] as const,
  featureFlagMisses: (universe: string) => ["feature-flag-misses", universe] as const,
  featureFlagCheck: (key: string) => ["feature-flag-check", key] as const,

  // Vehicles
  vehiclesByUser: (userId?: string, page?: number) =>
    userId !== undefined
      ? (["vehicles-by-user", userId, page] as const)
      : (["vehicles-by-user"] as const),

  // Counters
  counters: (params: unknown) => ["counters", params] as const,
  counterDefinitions: (universe?: string) => ["counter-definitions", universe ?? "all"] as const,
  counterTotals: (params: unknown) => ["counter-totals", params] as const,
  counterUserRanking: (params: unknown) => ["counter-user-ranking", params] as const,

  // Safety
  safetyReportsDetailed: (status: string, page: number) =>
    ["safety-reports-detailed", status, page] as const,
  safetyReportQueueSummary: () => ["safety-report-queue-summary"] as const,
  enforcements: (active: boolean, page: number) =>
    ["enforcements", active, page] as const,
  userEnforcements: (userId: string) => ["user-enforcements", userId] as const,
  userEnforcementSummaries: (enforcedOnly: boolean, page: number) =>
    ["user-enforcement-summaries", enforcedOnly, page] as const,

  // Map
  mapObjs: () => ["map-objs"] as const,
  liveLocations: (lat: number, lng: number, radius_m: number) =>
    ["live-locations", lat, lng, radius_m] as const,
  photoSpots: (lat: number, lng: number, radius_m?: number) =>
    (radius_m !== undefined ? ["photo-spots", lat, lng, radius_m] : ["photo-spots", lat, lng]) as const,
  photoSpotsByUser: (userId: string, page?: number) =>
    (page !== undefined ? ["photo-spots-by-user", userId, page] : ["photo-spots-by-user", userId]) as const,
  loginHeatmap: (resolution: number, days: number, minCount: number) =>
    ["login-heatmap", resolution, days, minCount] as const,

  // Waitlist
  waitlist: (page: number, sortBy?: string, sortDesc?: boolean) =>
    ["waitlist", page, sortBy, sortDesc] as const,
  waitlistHistory: (days: number) => ["waitlist-history", days] as const,

  // Permissions
  myPermissions: () => ["my-permissions"] as const,
  permissionsTemplate: () => ["permissions-template"] as const,

  // Special users
  specialUsers: () => ["special-users"] as const,
  specialUser: (userId: string) => ["special-user", userId] as const,

  // Costs
  adminCosts: (days: number) => ["admin-costs", days] as const,

  // Configs
  configTypes: () => ["config-types"] as const,
  configLatest: (type: string, env: string) => ["config-latest", type, env] as const,
  configHistory: (type: string, env: string) => ["config-history", type, env] as const,

  // Announcements
  announcements: () => ["announcements"] as const,
  locales: () => ["locales"] as const,

  // Sharding
  shardingServers: () => ["sharding-servers"] as const,

  // Storage
  adminStorageList: (prefix: string) => ["admin-storage-list", prefix] as const,
  adminStorageListAll: (prefix: string, pageToken: string) =>
    ["admin-storage-list-all", prefix, pageToken] as const,
};
